import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const ForgotPasswordPage = () => {
  const { t, lang, setLang, LANGS } = useI18n();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/auth/password-reset/request', { email });
      setSubmitted(true);
      toast.success('E-Mail gesendet!');
    } catch (error) {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Language Selector - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          className="bg-white/90 backdrop-blur border border-gray-300 rounded-md px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="language-selector"
        >
          {LANGS.map((l) => (
            <option key={l.code} value={l.code}>
              {l.flag} {l.label}
            </option>
          ))}
        </select>
      </div>

      {/* Mobile Hero Image */}
      <div
        className="lg:hidden h-48 relative"
        style={{
          backgroundImage: 'url(https://customer-assets-agu9un31.emergentagent.net/job_79549384-9d12-485d-8143-ee256e051cae/artifacts/1s5py9ht_ChatGPT%20Image%2026.%20Juli%202026%2C%2017_54_31.png)',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#1a1a1a',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
      </div>

      {/* Desktop Hero Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-center justify-center"
        style={{
          backgroundColor: '#1a1a1a',
        }}
      >
        <div
          className="w-full h-full"
          style={{
            backgroundImage: 'url(https://customer-assets-agu9un31.emergentagent.net/job_79549384-9d12-485d-8143-ee256e051cae/artifacts/1s5py9ht_ChatGPT%20Image%2026.%20Juli%202026%2C%2017_54_31.png)',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_barber-supply/artifacts/e1ft8m5t_ED_STEEL_Logo_White_BG_Print.png" 
              alt="ED STEEL" 
              className="h-24 w-auto mx-auto mb-6"
            />
          </div>

          {!submitted ? (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-heading font-bold mb-2">{t('forgotPassword')}</h2>
                <p className="text-muted-foreground">
                  {t('fpIntro')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 mt-2"
                    placeholder="ihre@email.de"
                    data-testid="email-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-lg font-heading"
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? t('fpSending') : t('fpSendLink')}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold mb-2">{t('fpEmailSent')}</h2>
              <p className="text-muted-foreground mb-6">
                {t('fpIfAccount')} <strong>{email}</strong> {t('fpInfoRest')}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {t('fpSpam')}
              </p>
            </div>
          )}

          <div className="mt-8 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center text-primary font-medium hover:underline"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
